/**
 * SignalEditorDialog — the structured editor behind New / Edit.
 *
 * Structured, not free-text, and that is the whole point: entry conditions,
 * exit conditions, parameters and evidence are REPEATABLE ROWS, so a later
 * feature can compose a strategy out of several entries without first having to
 * parse prose. It is still only a description — a parameter row is a recorded
 * value plus the settings path it lives at, never something that gets evaluated.
 *
 * Every list uses the same RowList control, so "add a row, drag it up, delete
 * it" works identically in all eight sections rather than eight times slightly
 * differently.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import toast from 'react-hot-toast';
import { useAppTheme } from '../../hooks/useAppTheme';
import { BUILTIN_INTERVALS, intervalLabel } from '../../utils/intervalCatalog';
import {
  GoldenSignalRow, GoldenSignalDraft, GoldenSignalPayload,
  GS_DIRECTIONS, GS_STATUSES, GS_DIRECTION_HELP, GS_STATUS_HELP,
  GsDirection, GsStatus, emptyPayload, hydratePayload,
} from '../../services/goldenSignals.service';

/* ─── a generic repeatable-row list ────────────────────────────────────────── */

interface RowListProps<T> {
  title: string;
  help: string;
  rows: T[];
  blank: () => T;
  onChange: (rows: T[]) => void;
  /** Render the editable fields of one row. */
  render: (row: T, set: (patch: Partial<T>) => void) => React.ReactNode;
  addLabel?: string;
}

function RowList<T>({ title, help, rows, blank, onChange, render, addLabel }: RowListProps<T>) {
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {help}
      </Typography>
      {rows.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.25,
            p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {render(row, (patch) => {
              const next = rows.slice();
              next[i] = { ...(next[i] as any), ...(patch as any) };
              onChange(next);
            })}
          </Box>
          <Stack sx={{ pt: 0.5 }}>
            <Tooltip title="Move up"><span>
              <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
                <ArrowUpwardIcon fontSize="inherit" />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Move down"><span>
              <IconButton size="small" disabled={i === rows.length - 1} onClick={() => move(i, 1)}>
                <ArrowDownwardIcon fontSize="inherit" />
              </IconButton>
            </span></Tooltip>
            <Tooltip title="Remove this row">
              <IconButton size="small" onClick={() => onChange(rows.filter((_, k) => k !== i))}>
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...rows, blank()])}>
        {addLabel || 'Add'}
      </Button>
    </Box>
  );
}

/* ─── the dialog ───────────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  /** null = creating a new entry. */
  row: GoldenSignalRow | null;
  /** Overlay keys the Charts page understands, for the indicator picker. */
  overlayOptions: Array<{ key: string; label: string }>;
  strategyOptions: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSave: (draft: GoldenSignalDraft) => Promise<void>;
}

const SignalEditorDialog: React.FC<Props> = ({
  open, row, overlayOptions, strategyOptions, onClose, onSave,
}) => {
  const { C } = useAppTheme();
  const [name, setName] = useState('');
  const [direction, setDirection] = useState<GsDirection>('LONG');
  const [status, setStatus] = useState<GsStatus>('DRAFT');
  const [summary, setSummary] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [p, setP] = useState<GoldenSignalPayload>(emptyPayload());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(row?.name || '');
    setDirection(row?.direction || 'LONG');
    setStatus(row?.status || 'DRAFT');
    setSummary(row?.summary || '');
    setTags(row?.tags || []);
    setP(row ? hydratePayload(row.payload) : emptyPayload());
    setSaving(false);
  }, [open, row]);

  const patch = (x: Partial<GoldenSignalPayload>) => setP(prev => ({ ...prev, ...x }));
  const patchChart = (x: Partial<NonNullable<GoldenSignalPayload['chart']>>) =>
    setP(prev => ({ ...prev, chart: { ...prev.chart!, ...x } }));
  const patchTf = (x: Partial<NonNullable<GoldenSignalPayload['timeframes']>>) =>
    setP(prev => ({ ...prev, timeframes: { ...prev.timeframes!, ...x } }));
  const patchMark = (x: Partial<NonNullable<GoldenSignalPayload['mark']>>) =>
    setP(prev => ({ ...prev, mark: { ...prev.mark!, ...x } }));

  const overlayByKey = useMemo(
    () => new Map(overlayOptions.map(o => [o.key, o.label])), [overlayOptions],
  );

  const save = async () => {
    if (!name.trim()) { toast.error('Give the signal a name first.'); return; }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), direction, status, summary: summary.trim(), tags, payload: p });
    } finally {
      setSaving(false);
    }
  };

  const tfField = (
    label: string,
    key: 'measuredOn' | 'alsoWorks' | 'weakOn',
    help: string,
  ) => (
    <Autocomplete
      multiple size="small" sx={{ flex: 1, minWidth: 210 }}
      options={[...BUILTIN_INTERVALS]}
      value={p.timeframes?.[key] || []}
      getOptionLabel={(o) => intervalLabel(o as number)}
      onChange={(_, v) => patchTf({ [key]: v as number[] } as any)}
      renderInput={(params) => <TextField {...params} label={label} helperText={help} />}
    />
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        {row ? 'Edit signal' : 'New signal'}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 400 }}>
          A description, not a rule the app runs. Nothing saved here places an order or starts a scan.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* ---- identity ---- */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            size="small" label="Name" value={name} onChange={e => setName(e.target.value)}
            sx={{ flex: '2 1 280px' }} required autoFocus
            placeholder="e.g. BW - BBR x Wave oversold turn"
          />
          <TextField
            select size="small" label="Direction" value={direction}
            onChange={e => setDirection(e.target.value as GsDirection)}
            sx={{ flex: '1 1 150px' }} helperText={GS_DIRECTION_HELP[direction]}
          >
            {GS_DIRECTIONS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Status" value={status}
            onChange={e => setStatus(e.target.value as GsStatus)}
            sx={{ flex: '1 1 150px' }} helperText={GS_STATUS_HELP[status]}
          >
            {GS_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Stack>

        <TextField
          size="small" fullWidth multiline minRows={2} label="Summary" sx={{ mb: 2 }}
          value={summary} onChange={e => setSummary(e.target.value)}
          placeholder="One paragraph: what the signal is and why it is worth keeping."
        />

        <Autocomplete
          multiple freeSolo size="small" options={[] as string[]} value={tags}
          onChange={(_, v) => setTags((v as string[]).map(t => t.trim().toLowerCase()).filter(Boolean))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
            ))}
          renderInput={(params) => (
            <TextField {...params} label="Tags" helperText="Type and press Enter. Used by the filter on the list." />
          )}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2.5 }} />

        {/* ---- the chart it lives on ---- */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Where to see it</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          Builds the &quot;show me this on a chart&quot; link. The indicators are switched ON when that link
          is followed; nothing is switched off.
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <TextField
            size="small" label="Symbol" sx={{ flex: '1 1 160px' }}
            value={p.chart?.defaultSymbol || ''}
            onChange={e => patchChart({ defaultSymbol: e.target.value.toUpperCase().trim() })}
            placeholder="BTCUSD"
          />
          <TextField
            select size="small" label="Interval" sx={{ flex: '1 1 130px' }}
            value={p.chart?.defaultInterval || 1440}
            onChange={e => patchChart({ defaultInterval: Number(e.target.value) })}
          >
            {BUILTIN_INTERVALS.map(m => <MenuItem key={m} value={m}>{intervalLabel(m)}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Candle" sx={{ flex: '1 1 130px' }}
            value={p.chart?.candle || 'Regular'}
            onChange={e => patchChart({ candle: e.target.value })}
          >
            {['Regular', 'HA', 'CLH', 'CLN', 'CSH', 'CSL'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Chart strategy" sx={{ flex: '1 1 170px' }}
            value={p.chart?.strategy || ''}
            onChange={e => patchChart({ strategy: e.target.value || null })}
            helperText="Optional"
          >
            <MenuItem value="">None</MenuItem>
            {strategyOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Stack>

        <Autocomplete
          multiple size="small" sx={{ mb: 3 }}
          options={overlayOptions.map(o => o.key)}
          value={p.chart?.overlays || []}
          getOptionLabel={(k) => overlayByKey.get(k) || k}
          onChange={(_, v) => {
            const keys = v as string[];
            patchChart({ overlays: keys });
            // Keep the "What it needs" list in step with the chips, so the two
            // cannot disagree about which indicators the signal requires.
            patch({
              indicators: keys.map(k => {
                const had = (p.indicators || []).find(i => i.key === k);
                return had || { key: k, label: overlayByKey.get(k) || k, note: '' };
              }),
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params} label="Indicators the signal needs"
              helperText="Chart chips. These are what the link turns on, and what the entry lists under 'What it needs'."
            />
          )}
        />

        <RowList
          title="Indicator notes"
          help="Optional — anything worth knowing about an indicator above."
          rows={p.indicators || []}
          blank={() => ({ key: '', label: '', note: '' })}
          onChange={(rows) => patch({ indicators: rows })}
          addLabel="Add an indicator"
          render={(r, set) => (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Chip key" value={r.key} sx={{ flex: '0 1 140px' }}
                onChange={e => set({ key: e.target.value.trim() })} />
              <TextField size="small" label="Label" value={r.label} sx={{ flex: '1 1 160px' }}
                onChange={e => set({ label: e.target.value })} />
              <TextField size="small" label="Note" value={r.note || ''} sx={{ flex: '2 1 240px' }}
                onChange={e => set({ note: e.target.value })} />
            </Stack>
          )}
        />

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {tfField('Measured on', 'measuredOn', 'Where the numbers below come from.')}
          {tfField('Also holds on', 'alsoWorks', 'Only if you actually checked.')}
          {tfField('Weak on', 'weakOn', 'Where it is break-even or worse.')}
        </Stack>
        <TextField
          size="small" fullWidth multiline minRows={2} label="Timeframe note" sx={{ mb: 2 }}
          value={p.timeframes?.note || ''} onChange={e => patchTf({ note: e.target.value })}
        />
        <Autocomplete
          multiple freeSolo size="small" options={[] as string[]} value={p.markets || []}
          onChange={(_, v) => patch({ markets: v as string[] })}
          renderInput={(params) => <TextField {...params} label="Markets" helperText="Type and press Enter." />}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2.5 }} />

        {/* ---- the mark ---- */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>The mark on the chart</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          What you are actually looking for on screen.
        </Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          <TextField size="small" label="Label" sx={{ flex: '1 1 120px' }}
            value={p.mark?.label || ''} onChange={e => patchMark({ label: e.target.value })}
            placeholder="BW" />
          <TextField size="small" label="Colour" sx={{ flex: '1 1 140px' }}
            value={p.mark?.color || ''} onChange={e => patchMark({ color: e.target.value.trim() })}
            placeholder="#26a69a"
            InputProps={{
              startAdornment: (
                <Box sx={{
                  width: 15, height: 15, mr: 1, borderRadius: '3px', flexShrink: 0,
                  bgcolor: p.mark?.color || 'transparent', border: '1px solid rgba(128,128,128,0.4)',
                }} />
              ),
            }} />
          <TextField select size="small" label="Shape" sx={{ flex: '1 1 140px' }}
            value={p.mark?.shape || 'arrowUp'} onChange={e => patchMark({ shape: e.target.value })}>
            {['arrowUp', 'arrowDown', 'circle', 'square'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Position" sx={{ flex: '1 1 140px' }}
            value={p.mark?.position || 'belowBar'} onChange={e => patchMark({ position: e.target.value })}>
            {['belowBar', 'aboveBar', 'inBar'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Stack>

        <Divider sx={{ mb: 2.5 }} />

        {/* ---- the rule ---- */}
        <RowList
          title="Entry conditions"
          help="Every condition that has to hold. One row each — including the qualifiers that are easy to forget, like 'closed bars only'."
          rows={p.entry || []}
          blank={() => ({ tag: '', label: '', desc: '' })}
          onChange={(rows) => patch({ entry: rows })}
          addLabel="Add an entry condition"
          render={(r, set) => (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Tag" value={r.tag || ''} sx={{ width: 110 }}
                  onChange={e => set({ tag: e.target.value })} />
                <TextField size="small" label="Condition" value={r.label} sx={{ flex: 1 }}
                  onChange={e => set({ label: e.target.value })} />
              </Stack>
              <TextField size="small" multiline minRows={2} label="Explanation" value={r.desc}
                onChange={e => set({ desc: e.target.value })} />
            </Stack>
          )}
        />

        <RowList
          title="Exit conditions"
          help="What closes it. Say which one wins when more than one could fire on the same bar."
          rows={p.exit || []}
          blank={() => ({ tag: '', label: '', desc: '' })}
          onChange={(rows) => patch({ exit: rows })}
          addLabel="Add an exit condition"
          render={(r, set) => (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="Tag" value={r.tag || ''} sx={{ width: 110 }}
                  onChange={e => set({ tag: e.target.value })} />
                <TextField size="small" label="Condition" value={r.label} sx={{ flex: 1 }}
                  onChange={e => set({ label: e.target.value })} />
              </Stack>
              <TextField size="small" multiline minRows={2} label="Explanation" value={r.desc}
                onChange={e => set({ desc: e.target.value })} />
            </Stack>
          )}
        />

        <RowList
          title="Parameters"
          help="The value, and where in the app that number lives — a settings path like settings.bbr.osLevel, or a file. A value with no source is a value nobody can check."
          rows={p.params || []}
          blank={() => ({ key: '', label: '', value: '', source: '' })}
          onChange={(rows) => patch({ params: rows })}
          addLabel="Add a parameter"
          render={(r, set) => (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Key" value={r.key} sx={{ flex: '0 1 120px' }}
                onChange={e => set({ key: e.target.value.trim() })} />
              <TextField size="small" label="Label" value={r.label} sx={{ flex: '1 1 180px' }}
                onChange={e => set({ label: e.target.value })} />
              <TextField size="small" label="Value" value={r.value} sx={{ flex: '0 1 110px' }}
                onChange={e => set({ value: e.target.value })} />
              <TextField size="small" label="Source" value={r.source} sx={{ flex: '2 1 240px' }}
                onChange={e => set({ source: e.target.value.trim() })}
                placeholder="settings.bbr.osLevel" />
            </Stack>
          )}
        />

        <RowList
          title="Evidence"
          help="What it measured, and on what. A baseline is what the same test scored doing nothing — without one a number means very little."
          rows={p.evidence || []}
          blank={() => ({ dataset: '', metric: '', value: '', baseline: '', win: '', note: '' })}
          onChange={(rows) => patch({ evidence: rows })}
          addLabel="Add a measurement"
          render={(r, set) => (
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <TextField size="small" label="Result" value={r.value} sx={{ flex: '0 1 120px' }}
                  onChange={e => set({ value: e.target.value })} placeholder="+3.60%" />
                <TextField size="small" label="Metric" value={r.metric} sx={{ flex: '1 1 190px' }}
                  onChange={e => set({ metric: e.target.value })} placeholder="mean return per trade" />
                <TextField size="small" label="Baseline" value={r.baseline || ''} sx={{ flex: '0 1 120px' }}
                  onChange={e => set({ baseline: e.target.value })} />
                <TextField size="small" label="Win rate" value={r.win || ''} sx={{ flex: '0 1 110px' }}
                  onChange={e => set({ win: e.target.value })} />
              </Stack>
              <TextField size="small" label="Dataset" value={r.dataset}
                onChange={e => set({ dataset: e.target.value })}
                placeholder="171 Delta symbols, about 102k daily bars" />
              <TextField size="small" multiline minRows={2} label="Note" value={r.note || ''}
                onChange={e => set({ note: e.target.value })} />
            </Stack>
          )}
        />

        <RowList
          title="What did not work"
          help="Variants you tried and measured dead. Worth as much as the ones that worked — it is what stops you re-testing them next month."
          rows={p.notWorking || []}
          blank={() => ({ label: '', desc: '' })}
          onChange={(rows) => patch({ notWorking: rows })}
          addLabel="Add a dead end"
          render={(r, set) => (
            <Stack spacing={1}>
              <TextField size="small" label="What was tried" value={r.label}
                onChange={e => set({ label: e.target.value })} />
              <TextField size="small" multiline minRows={2} label="What it measured" value={r.desc}
                onChange={e => set({ desc: e.target.value })} />
            </Stack>
          )}
        />

        <RowList
          title="Related"
          help="Other signals or strategies this leads to. A strategy id must be one the Charts page knows."
          rows={p.related || []}
          blank={() => ({ kind: 'strategy' as const, id: '', label: '' })}
          onChange={(rows) => patch({ related: rows })}
          addLabel="Add a link"
          render={(r, set) => (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField select size="small" label="Kind" value={r.kind} sx={{ flex: '0 1 130px' }}
                onChange={e => set({ kind: e.target.value as any })}>
                {['strategy', 'signal', 'doc'].map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </TextField>
              {r.kind === 'strategy' ? (
                <TextField select size="small" label="Strategy" value={r.id} sx={{ flex: '1 1 170px' }}
                  onChange={e => set({ id: e.target.value })}>
                  {strategyOptions.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
              ) : (
                <TextField size="small" label="Id" value={r.id} sx={{ flex: '1 1 170px' }}
                  onChange={e => set({ id: e.target.value.trim() })} />
              )}
              <TextField size="small" label="Label" value={r.label} sx={{ flex: '2 1 240px' }}
                onChange={e => set({ label: e.target.value })} />
            </Stack>
          )}
        />

        <Autocomplete
          multiple freeSolo size="small" options={[] as string[]} value={p.sources || []}
          onChange={(_, v) => patch({ sources: (v as string[]).map(s => s.trim()).filter(Boolean) })}
          renderInput={(params) => (
            <TextField {...params} label="Defined in"
              helperText="Repo paths that define the signal, e.g. frontend/src/utils/bbrWaveTrades.ts. Type and press Enter." />
          )}
          sx={{ mb: 3 }}
        />

        <TextField
          size="small" fullWidth multiline minRows={3} label="Notes"
          value={p.notes || ''} onChange={e => patch({ notes: e.target.value })}
          placeholder="Anything that does not fit above."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', color: C.neutral }}>
          Saving records a description. It arms nothing.
        </Typography>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (row ? 'Save changes' : 'Create signal')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignalEditorDialog;
